import React from 'react';
import { filterRoutineProducts } from '../../utils/routineProducts';

function productStepLabel(product) {
  if (!product) return null;
  if (typeof product === 'string') return product;
  return product.product_name || product.name || null;
}

function stepKindFromLabel(label) {
  const s = String(label || '').toLowerCase();
  if (/sun|spf|uv/.test(s)) return 'sunscreen';
  if (/clean|wash|foam|gel cleanser/.test(s)) return 'cleanser';
  if (/serum|vitamin|niacin|retinol|acid/.test(s)) return 'serum';
  if (/eye/.test(s)) return 'eye';
  if (/moist|cream|lotion|hydr/.test(s)) return 'moisturizer';
  if (/toner|essence|mist/.test(s)) return 'toner';
  if (/oil|balm/.test(s)) return 'oil';
  return 'product';
}

function StepGlyph({ kind }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: '1.9',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  switch (kind) {
    case 'sunscreen':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      );
    case 'cleanser':
      return (
        <svg {...common}>
          <path d="M7 10c0-3 2-5 5-5s5 2 5 5v8H7v-8z" />
          <path d="M9 21h6" />
        </svg>
      );
    case 'serum':
      return (
        <svg {...common}>
          <path d="M9 3h6" />
          <path d="M10 3v3.5L7 14a5 5 0 0 0 10 0l-3-7.5V3" />
        </svg>
      );
    case 'eye':
      return (
        <svg {...common}>
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
          <circle cx="12" cy="12" r="2.5" />
        </svg>
      );
    case 'moisturizer':
      return (
        <svg {...common}>
          <path d="M12 3s6 6.2 6 10a6 6 0 1 1-12 0c0-3.8 6-10 6-10z" />
        </svg>
      );
    case 'toner':
      return (
        <svg {...common}>
          <path d="M8 3h8v4l2 3v9a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-9l2-3V3z" />
          <path d="M8 10h8" />
        </svg>
      );
    case 'oil':
      return (
        <svg {...common}>
          <path d="M12 2c3 4 5 7 5 10a5 5 0 1 1-10 0c0-3 2-6 5-10z" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <rect x="7" y="3" width="10" height="18" rx="2" />
          <path d="M10 7h4" />
        </svg>
      );
  }
}

function buildProductSteps(products, resolveImage) {
  const rows = filterRoutineProducts(products);
  if (!rows.length) return [];
  return rows.slice(0, 5).map((product, i) => {
    const label = productStepLabel(product);
    const rawImg = product?.image_url || product?.image || product?.thumbnail || null;
    const imageUrl = resolveImage && rawImg ? resolveImage(rawImg) : (typeof rawImg === 'string' ? rawImg : null);
    return {
      key: `p-${product?.product_id || product?.id || i}`,
      label,
      imageUrl,
      kind: stepKindFromLabel(label),
    };
  }).filter((s) => s.label);
}

function RoutineColumn({ title, icon, steps }) {
  return (
    <div className="skin-report-routine-col">
      <h3 className="skin-report-routine-heading">
        <span className="skin-report-routine-heading-icon" aria-hidden="true">{icon}</span>
        {title}
      </h3>
      {steps.length ? (
        <ol className="skin-report-routine-list">
          {steps.map((step) => (
            <li key={step.key} className="skin-report-routine-step">
              <span className="skin-report-routine-thumb" aria-hidden="true">
                {step.imageUrl ? (
                  <img
                    src={step.imageUrl}
                    alt=""
                    className="skin-report-routine-thumb-img"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) fallback.hidden = false;
                    }}
                  />
                ) : null}
                <span className="skin-report-routine-thumb-fallback" hidden={Boolean(step.imageUrl)}>
                  <StepGlyph kind={step.kind} />
                </span>
              </span>
              <span className="skin-report-routine-step-label">{step.label}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="skin-report-routine-span skin-report-empty">No product assigned yet.</p>
      )}
    </div>
  );
}

const SUN_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

const MOON_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 14.5A8.5 8.5 0 1 1 9.5 3 7 7 0 0 0 21 14.5z" />
  </svg>
);

export default function SkinReportRoutine({
  morningProducts = [],
  eveningProducts = [],
  morningSteps = [],
  eveningSteps = [],
  resolveImage = null,
  loading = false,
}) {
  const morning = buildProductSteps(morningProducts, resolveImage);
  const evening = buildProductSteps(eveningProducts, resolveImage);

  if (loading) {
    return (
      <div className="skin-report-routine-span skin-routine-summary-loading">
        <div className="loading-spinner" />
        <p className="skin-routine-summary-loading-text">Generating recommended routine...</p>
      </div>
    );
  }

  if (!morning.length && !evening.length) {
    return <p className="skin-report-routine-span skin-report-empty">Routine will appear once product recommendations are ready.</p>;
  }

  return (
    <>
      <RoutineColumn title="Morning" icon={SUN_ICON} steps={morning} />
      <RoutineColumn title="Night" icon={MOON_ICON} steps={evening} />
    </>
  );
}
